import React from 'react';
import { useLocaleContext } from '../context/LocaleContext';

interface UserAvatarStackProps {
  maxAvatars?: number;
}

// Static data for social proof - eliminates async fetch and CLS
// These are decorative avatars - actual user count updated periodically
//
// PROVENANCE: 8,583 was confirmed accurate in the Aug 2026 site audit
// (remove-claude-watermarks patch §3). Source system and refresh cadence are
// still TBD — do not change this number without recording where the new figure
// comes from and how often it is refreshed, or it will silently go stale.
export const TOTAL_USERS = 8583;

// CLS FIX: Use inline SVG data URIs instead of external URLs
// External images (pravatar.cc, ui-avatars.com) require network requests → CLS
// Inline SVGs render instantly with the HTML
const createAvatarSvg = (initials: string, bgColor: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="20" fill="${bgColor}"/><text x="20" y="20" text-anchor="middle" dy=".35em" fill="white" font-family="system-ui,sans-serif" font-size="14" font-weight="600">${initials}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Static inline SVG avatars - no network requests, instant render
const AVATAR_URLS = [
  createAvatarSvg('JD', '#3B82F6'), // blue
  createAvatarSvg('MK', '#059669'), // green
  createAvatarSvg('AL', '#8B5CF6'), // purple
  createAvatarSvg('SR', '#EF4444'), // red
  createAvatarSvg('LP', '#F59E0B'), // amber
  createAvatarSvg('TC', '#06B6D4'), // cyan
  createAvatarSvg('RW', '#EC4899'), // pink
  createAvatarSvg('DP', '#10B981'), // emerald
];

export const UserAvatarStack: React.FC<UserAvatarStackProps> = ({ maxAvatars = 5 }) => {
  const { locale } = useLocaleContext();

  // Format user count with locale-specific thousands separator
  const getLocaleCode = () => {
    switch (locale) {
      case 'de': return 'de-DE';
      case 'pt-BR': return 'pt-BR';
      case 'ms-MY': return 'ms-MY';
      case 'es': return 'es-ES';
      case 'fr': return 'fr-FR';
      case 'ja': return 'ja-JP';
      case 'id': return 'id-ID';
      default: return 'en-US';
    }
  };
  const formattedCount = TOTAL_USERS.toLocaleString(getLocaleCode());

  // Localized "writers trust us" text
  const getTrustText = () => {
    switch (locale) {
      case 'de': return 'Nutzer vertrauen uns';
      case 'pt-BR': return 'escritores confiam em nós';
      case 'ms-MY': return 'penulis mempercayai kami';
      case 'es': return 'escritores confían en nosotros';
      case 'fr': return 'rédacteurs nous font confiance';
      case 'ja': return '人のライターが信頼';
      case 'id': return 'penulis mempercayai kami';
      default: return 'writers trust us';
    }
  };

  return (
    <div className="flex items-center gap-4 min-h-[44px]">
      {/* Stacked Avatars */}
      <div className="flex -space-x-3">
        {AVATAR_URLS.slice(0, maxAvatars).map((url, index) => (
          <div
            key={index}
            className="relative"
            style={{ zIndex: maxAvatars - index }}
          >
            {/* Inline SVG data URI - renders instantly, no network request */}
            <img
              src={url}
              alt=""
              role="presentation"
              width="40"
              height="40"
              className="w-10 h-10 rounded-full border-2 border-white dark:border-charcoal-900"
            />
          </div>
        ))}
      </div>

      {/* User Count */}
      <div className="text-base font-semibold text-neutral-900 dark:text-white">
        <span className="font-bold">{formattedCount}</span>{' '}
        <span className="text-neutral-600 dark:text-neutral-400">
          {getTrustText()}
        </span>
      </div>
    </div>
  );
};
